import { action, makeObservable } from "mobx";
import { View } from "tamagui";
import { TKey } from "../../../../shared/translations/translationKeys";
import { ActivityEventType } from "../../../activities/types";
import type { Api } from "../../../core/apis";
import { formatDateRange } from "../../../core/dayJs";
import { groupDateRanges } from "../../../core/groupDates";
import type { Stores } from "../../../core/stores";
import BaseModuleDataModel, {
  type BaseModuleModelData,
} from "../../core/BaseModuleDataModel";

export interface CalendarItemModuleModelData extends BaseModuleModelData {
  userId: string;
  selectedDates: string[];
}

export default class CalendarItem extends BaseModuleDataModel<CalendarItemModuleModelData> {
  constructor(stores: Stores, api: Api, data: CalendarItemModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      userId: data.userId ?? 0,
      selectedDates: data.selectedDates ?? [],
    });

    makeObservable(this);
  }

  @action setSelectedDates(selectedDates: string[]) {
    this.data.selectedDates = Array.from(new Set(selectedDates));

    this.updateTimestamp();
    this.api.modulesDataApi.set(this);
    this.module.updateSnapshotWithoutSave();
    this.module.save();

    if (selectedDates.length) {
      this.module.postActivityEvent(
        ActivityEventType.DataChanged,
        TKey.ActivityCalendarDatesSet,
        {
          dates: groupDateRanges(this.data.selectedDates.slice())
            .map((range) => formatDateRange(range, true))
            .join(", "),
        },
      );
    }
    this.sendTelemetryEvent("updated_dates");
  }

  toFireStore(): Omit<CalendarItemModuleModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...super.toFireStore(),
      userId: this.data.userId,
      selectedDates: this.data.selectedDates,
    };
  }

  render() {
    return <View />;
  }

  static is(
    module: BaseModuleDataModel<BaseModuleModelData>,
  ): module is CalendarItem {
    return module instanceof CalendarItem;
  }
}
